/**
 * Keyboard Shortcut System for NeuroPsyMap
 * Enables full autonomous exploration of the interface via keyboard.
 * All major features are accessible without mouse interaction.
 */

export type ShortcutCategory =
  | 'simulation'
  | 'navigation'
  | 'view'
  | 'audio'
  | 'data'
  | 'help'
  | 'debug';

export interface ShortcutAction {
  id: string;
  keys: string;
  description: string;
  category: ShortcutCategory;
  handler: () => void;
  enabled?: () => boolean;
}

export interface ShortcutGroup {
  category: ShortcutCategory;
  label: string;
  shortcuts: ShortcutAction[];
}

export class KeyboardShortcutSystem {
  private shortcuts: Map<string, ShortcutAction> = new Map();
  private groups: Map<ShortcutCategory, ShortcutGroup> = new Map();
  private boundHandler: (e: KeyboardEvent) => void;
  private enabled: boolean = true;
  private helpVisible: boolean = false;
  private helpOverlay: HTMLElement | null = null;

  constructor() {
    this.boundHandler = this.handleKeyDown.bind(this);
    document.addEventListener('keydown', this.boundHandler);
    this.initializeDefaults();
  }

  private initializeDefaults(): void {
    // Simulation controls
    this.register({
      id: 'sim.togglePlay',
      keys: 'Space',
      description: 'Play/Pause simulation',
      category: 'simulation',
      handler: () => this.emit('sim:togglePlay'),
    });
    this.register({
      id: 'sim.restart',
      keys: 'Ctrl+Shift+R',
      description: 'Restart simulation',
      category: 'simulation',
      handler: () => this.emit('sim:restart'),
    });
    this.register({
      id: 'sim.stepForward',
      keys: 'ArrowRight',
      description: 'Step forward one frame',
      category: 'simulation',
      handler: () => this.emit('sim:stepForward'),
    });
    this.register({
      id: 'sim.faster',
      keys: ']',
      description: 'Increase simulation speed',
      category: 'simulation',
      handler: () => this.emit('sim:speedUp'),
    });
    this.register({
      id: 'sim.slower',
      keys: '[',
      description: 'Decrease simulation speed',
      category: 'simulation',
      handler: () => this.emit('sim:slowDown'),
    });

    // Navigation
    this.register({
      id: 'nav.toggleSidebar',
      keys: 'b',
      description: 'Toggle sidebar panel',
      category: 'navigation',
      handler: () => this.emit('nav:toggleSidebar'),
    });
    this.register({
      id: 'nav.toggleFullscreen',
      keys: 'f',
      description: 'Toggle fullscreen mode',
      category: 'navigation',
      handler: () => this.emit('nav:toggleFullscreen'),
    });
    this.register({
      id: 'nav.nextSection',
      keys: 'Tab',
      description: 'Focus next UI section',
      category: 'navigation',
      handler: () => this.emit('nav:nextSection'),
    });
    this.register({
      id: 'nav.prevSection',
      keys: 'Shift+Tab',
      description: 'Focus previous UI section',
      category: 'navigation',
      handler: () => this.emit('nav:prevSection'),
    });

    // View controls
    this.register({
      id: 'view.zoomIn',
      keys: '=',
      description: 'Zoom in on network',
      category: 'view',
      handler: () => this.emit('view:zoomIn'),
    });
    this.register({
      id: 'view.zoomOut',
      keys: '-',
      description: 'Zoom out from network',
      category: 'view',
      handler: () => this.emit('view:zoomOut'),
    });
    this.register({
      id: 'view.resetView',
      keys: '0',
      description: 'Reset camera view',
      category: 'view',
      handler: () => this.emit('view:reset'),
    });
    this.register({
      id: 'view.rotateCW',
      keys: 'e',
      description: 'Rotate view clockwise',
      category: 'view',
      handler: () => this.emit('view:rotateCW'),
    });
    this.register({
      id: 'view.rotateCCW',
      keys: 'q',
      description: 'Rotate view counter-clockwise',
      category: 'view',
      handler: () => this.emit('view:rotateCCW'),
    });
    this.register({
      id: 'view.tiltUp',
      keys: 'ArrowUp',
      description: 'Tilt view upward',
      category: 'view',
      handler: () => this.emit('view:tiltUp'),
    });
    this.register({
      id: 'view.tiltDown',
      keys: 'ArrowDown',
      description: 'Tilt view downward',
      category: 'view',
      handler: () => this.emit('view:tiltDown'),
    });
    this.register({
      id: 'view.toggle3D',
      keys: '3',
      description: 'Toggle 2D/3D projection',
      category: 'view',
      handler: () => this.emit('view:toggle3D'),
    });

    // Audio controls
    this.register({
      id: 'audio.toggleMute',
      keys: 'm',
      description: 'Mute/Unmute audio',
      category: 'audio',
      handler: () => this.emit('audio:toggleMute'),
    });
    this.register({
      id: 'audio.volUp',
      keys: 'Ctrl+=',
      description: 'Increase volume',
      category: 'audio',
      handler: () => this.emit('audio:volumeUp'),
    });
    this.register({
      id: 'audio.volDown',
      keys: 'Ctrl+-',
      description: 'Decrease volume',
      category: 'audio',
      handler: () => this.emit('audio:volumeDown'),
    });

    // Data operations
    this.register({
      id: 'data.save',
      keys: 'Ctrl+s',
      description: 'Save simulation state',
      category: 'data',
      handler: () => this.emit('data:save'),
    });
    this.register({
      id: 'data.load',
      keys: 'Ctrl+o',
      description: 'Load simulation state',
      category: 'data',
      handler: () => this.emit('data:load'),
    });
    this.register({
      id: 'data.export',
      keys: 'Ctrl+e',
      description: 'Export data',
      category: 'data',
      handler: () => this.emit('data:export'),
    });
    this.register({
      id: 'data.import',
      keys: 'Ctrl+i',
      description: 'Import data',
      category: 'data',
      handler: () => this.emit('data:import'),
    });

    // Help & Debug
    this.register({
      id: 'help.show',
      keys: '?',
      description: 'Show keyboard shortcuts',
      category: 'help',
      handler: () => this.toggleHelp(),
    });
    this.register({
      id: 'debug.togglePerf',
      keys: 'Ctrl+Shift+P',
      description: 'Toggle performance monitor',
      category: 'debug',
      handler: () => this.emit('debug:togglePerf'),
    });
    this.register({
      id: 'debug.toggleLog',
      keys: '`',
      description: 'Toggle debug log panel',
      category: 'debug',
      handler: () => this.emit('debug:toggleLog'),
    });
    this.register({
      id: 'debug.dumpState',
      keys: 'Ctrl+Shift+D',
      description: 'Dump full state to console',
      category: 'debug',
      handler: () => this.emit('debug:dumpState'),
    });
  }

  register(action: ShortcutAction): void {
    const key = this.normalizeKeys(action.keys);
    this.shortcuts.set(key, action);

    const existing = this.groups.get(action.category);
    if (existing) {
      existing.shortcuts.push(action);
    } else {
      this.groups.set(action.category, {
        category: action.category,
        label: this.getCategoryLabel(action.category),
        shortcuts: [action],
      });
    }
  }

  unregister(id: string): void {
    for (const [key, action] of this.shortcuts) {
      if (action.id === id) {
        this.shortcuts.delete(key);
        const group = this.groups.get(action.category);
        if (group) {
          group.shortcuts = group.shortcuts.filter((s) => s.id !== id);
        }
        return;
      }
    }
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  destroy(): void {
    document.removeEventListener('keydown', this.boundHandler);
    this.shortcuts.clear();
    this.groups.clear();
    this.hideHelp();
  }

  getShortcutsByCategory(): ShortcutGroup[] {
    return Array.from(this.groups.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (!this.enabled) return;

    // Ignore shortcuts when typing in input fields
    const target = e.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.isContentEditable
    ) {
      if (e.key !== 'Escape') return;
    }

    // Handle Escape to close help
    if (e.key === 'Escape' && this.helpVisible) {
      this.hideHelp();
      return;
    }

    const keyCombo = this.buildKeyCombo(e);
    const action = this.shortcuts.get(keyCombo);

    if (action) {
      if (action.enabled && !action.enabled()) return;
      e.preventDefault();
      e.stopPropagation();
      action.handler();
    }
  }

  private buildKeyCombo(e: KeyboardEvent): string {
    const parts: string[] = [];
    if (e.ctrlKey || e.metaKey) parts.push('Ctrl');
    if (e.shiftKey) parts.push('Shift');
    if (e.altKey) parts.push('Alt');

    // Normalize key names
    const key = e.key === ' ' ? 'Space' : e.key.length === 1 ? e.key.toUpperCase() : e.key;
    parts.push(key);

    return parts.join('+');
  }

  private normalizeKeys(keys: string): string {
    return keys.split('+').map((k) => k.trim()).join('+');
  }

  private getCategoryLabel(category: ShortcutCategory): string {
    const labels: Record<ShortcutCategory, string> = {
      simulation: 'Simulation',
      navigation: 'Navigation',
      view: 'View Controls',
      audio: 'Audio',
      data: 'Data Operations',
      help: 'Help',
      debug: 'Debug',
    };
    return labels[category];
  }

  // Event emission (uses custom DOM events for decoupled communication)
  private emit(eventName: string, detail?: unknown): void {
    window.dispatchEvent(
      new CustomEvent(eventName, { detail, bubbles: true })
    );
  }

  // Help overlay
  private toggleHelp(): void {
    if (this.helpVisible) {
      this.hideHelp();
    } else {
      this.showHelp();
    }
  }

  private showHelp(): void {
    this.helpVisible = true;
    if (this.helpOverlay) {
      this.helpOverlay.style.display = 'flex';
      return;
    }
    this.helpOverlay = this.buildHelpOverlay();
    document.body.appendChild(this.helpOverlay);
  }

  private hideHelp(): void {
    this.helpVisible = false;
    if (this.helpOverlay) {
      this.helpOverlay.style.display = 'none';
    }
  }

  private buildHelpOverlay(): HTMLElement {
    const overlay = document.createElement('div');
    overlay.id = 'shortcut-help-overlay';
    overlay.style.cssText = `
      position: fixed; inset: 0; z-index: 10000;
      display: flex; align-items: center; justify-content: center;
      background: rgba(0,0,0,0.75); backdrop-filter: blur(4px);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    `;

    const modal = document.createElement('div');
    modal.style.cssText = `
      background: #1f2937; border-radius: 12px; padding: 24px 32px;
      max-width: 640px; width: 90%; max-height: 80vh; overflow-y: auto;
      box-shadow: 0 20px 60px rgba(0,0,0,0.5); border: 1px solid #374151;
    `;

    const title = document.createElement('h2');
    title.textContent = 'Keyboard Shortcuts';
    title.style.cssText = 'margin: 0 0 4px; font-size: 1.3rem; color: #f9fafb;';

    const subtitle = document.createElement('p');
    subtitle.textContent = 'Press ? to toggle this help. Press Escape to close.';
    subtitle.style.cssText = 'margin: 0 0 20px; font-size: 0.85rem; color: #9ca3af;';

    modal.appendChild(title);
    modal.appendChild(subtitle);

    const groups = this.getShortcutsByCategory();
    for (const group of groups) {
      const section = document.createElement('div');
      section.style.marginBottom = '16px';

      const header = document.createElement('h3');
      header.textContent = group.label;
      header.style.cssText = 'margin: 0 0 8px; font-size: 0.95rem; color: #818cf8; border-bottom: 1px solid #374151; padding-bottom: 4px;';
      section.appendChild(header);

      for (const shortcut of group.shortcuts) {
        const row = document.createElement('div');
        row.style.cssText = 'display: flex; justify-content: space-between; padding: 4px 0; font-size: 0.85rem;';

        const keyEl = document.createElement('kbd');
        keyEl.textContent = shortcut.keys;
        keyEl.style.cssText = `
          background: #374151; padding: 2px 8px; border-radius: 4px;
          font-family: 'Consolas', 'Monaco', monospace; font-size: 0.8rem;
          color: #e0b0ff; border: 1px solid #4b5563; min-width: 80px; text-align: center;
        `;

        const desc = document.createElement('span');
        desc.textContent = shortcut.description;
        desc.style.color = '#d1d5db';

        row.appendChild(keyEl);
        row.appendChild(desc);
        section.appendChild(row);
      }
      modal.appendChild(section);
    }

    overlay.appendChild(modal);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.hideHelp();
    });
    return overlay;
  }
}

// Singleton instance
let instance: KeyboardShortcutSystem | null = null;

export function getKeyboardShortcutSystem(): KeyboardShortcutSystem {
  if (!instance) {
    instance = new KeyboardShortcutSystem();
  }
  return instance;
}
